export type ExecutionStatus = 'idle' | 'running' | 'success' | 'error';

export interface RunResult {
  ok: boolean;
  httpStatus: number;
  data: unknown;
  errorMessage?: string;
  executionId?: string;
}

export const PIPELINE_STEPS = [
  { id: 'receive', label: 'Receive PDF & prompt' },
  { id: 'extract', label: 'Extract text from PDF' },
  { id: 'ai', label: 'Generate lesson (OpenAI)' },
  { id: 'topics', label: 'Parse & split topics' },
  { id: 'skybox', label: 'Generate skyboxes' },
  { id: 'save', label: 'Save to Firebase & Sheets' },
] as const;

export type PipelineStepId = (typeof PIPELINE_STEPS)[number]['id'];

export interface N8nExecution {
  id: string;
  finished: boolean;
  status: 'running' | 'success' | 'error' | 'waiting';
  startedAt: string;
  stoppedAt?: string;
  data?: {
    resultData?: {
      runData?: Record<
        string,
        Array<{
          startTime: number;
          executionTime?: number;
          data?: {
            main?: any[][];
          };
          error?: {
            message?: string;
            stack?: string;
            description?: string;
          };
        }>
      >;
      error?: {
        message: string;
        stack?: string;
      };
    };
  };
}

export interface N8nExecutionListItem {
  id: string;
  startedAt: string;
  stoppedAt?: string;
  status: 'running' | 'success' | 'error' | 'waiting';
  mode?: string;
  workflowId?: string;
}

const WEBHOOK_URL = window.VITE_ENV.VITE_N8N_WEBHOOK_URL as string | undefined;
const API_PROXY_URL =
  (window.VITE_ENV.VITE_API_PROXY_URL as string | undefined) || '/api';

export const canPollExecution = Boolean(API_PROXY_URL);

async function getProxyAuthHeaders(): Promise<HeadersInit> {
  const { auth } = await import('../config/firebase');
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Warn once, and only when the automation is actually invoked.
 *
 * This used to run at module scope, so every page that transitively imported this service
 * logged the warning on load — including the VR lesson player, which never triggers an n8n
 * workflow. Importing a module isn't using the feature, so the warning belonged at the call
 * site rather than in the console of unrelated screens.
 */
let warnedMissingWebhook = false;
function warnIfWebhookMissing(): void {
  if (WEBHOOK_URL || warnedMissingWebhook) return;
  warnedMissingWebhook = true;
  console.warn(
    'VITE_N8N_WEBHOOK_URL is not set. Configure it in a .env file to connect the UI to your n8n workflow.',
  );
}

export async function triggerAutomation(params: {
  pdfFile: File | null;
  prompt: string;
  language: string;
  curriculum: string;
  classLevel: string;
  subject: string;
}): Promise<RunResult> {
  if (!WEBHOOK_URL) {
    warnIfWebhookMissing();
    return {
      ok: false,
      httpStatus: 0,
      data: null,
      errorMessage: 'N8N webhook URL is not configured on the frontend.',
    };
  }

  const formData = new FormData();
  if (params.pdfFile) {
    formData.append('file', params.pdfFile, params.pdfFile.name);
  }
  formData.append('prompt', params.prompt);
  formData.append('language', params.language);
  formData.append('curriculum', params.curriculum);
  formData.append('class', params.classLevel);
  formData.append('subject', params.subject);

  const response = await fetch(WEBHOOK_URL, {
    method: 'POST',
    body: formData,
  });

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // ignore JSON parse errors; body stays null
  }

  const data = body as Record<string, unknown> | null;
  const executionId =
    data && typeof data.executionId === 'string' ? data.executionId : undefined;

  const errorMessage =
    !response.ok
      ? (data && typeof data.message === 'string'
          ? data.message
          : `Request failed with status ${response.status}`)
      : undefined;

  return {
    ok: response.ok,
    httpStatus: response.status,
    data: body,
    errorMessage,
    executionId,
  };
}

async function fetchN8nProxy(path: string): Promise<Response | null> {
  const proxyBase = API_PROXY_URL.replace(/\/$/, '');
  const url = `${proxyBase}${path.startsWith('/') ? path : `/${path}`}`;
  try {
    return await fetch(url, { headers: await getProxyAuthHeaders() });
  } catch {
    return null;
  }
}

export async function getExecutionStatus(executionId: string): Promise<N8nExecution | null> {
  const res = await fetchN8nProxy(`/n8n/executions/${encodeURIComponent(executionId)}`);
  if (!res?.ok) return null;
  return (await res.json()) as N8nExecution;
}

export async function fetchExecutionDetail(executionId: string): Promise<N8nExecution | null> {
  return getExecutionStatus(executionId);
}

export async function listExecutions(limit = 10): Promise<N8nExecutionListItem[] | null> {
  const res = await fetchN8nProxy(`/n8n/executions?limit=${encodeURIComponent(limit)}`);
  if (!res?.ok) return null;
  const json = (await res.json()) as { data?: N8nExecutionListItem[] } | N8nExecutionListItem[];
  if (Array.isArray(json)) return json;
  if (json && Array.isArray(json.data)) return json.data;
  return null;
}
